import { Button, Table, Tag, Tooltip, notification } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import PageTitle from 'components/page-title';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LeaveType } from 'types/leave-request';
import LeaveTypeModal from './components/leave-type-modal';
import { mapLeaveRequestError } from 'pages/leave-request-manager/helpers/leave-request-error';

function extractLeaveTypes(res: any): LeaveType[] {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  return [];
}

const LeaveTypeManager = () => {
  const [loading, setLoading] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<LeaveType | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((key) => key + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchLeaveTypes = async () => {
      setLoading(true);
      try {
        const res = await AxiosUtils.get(API.leaveTypes, { includeInactive: true });
        if (!cancelled) setLeaveTypes(extractLeaveTypes(res));
      } catch (err) {
        if (!cancelled) notification.error({ message: mapLeaveRequestError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLeaveTypes();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const columns: ColumnsType<LeaveType> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Tên loại nghỉ', dataIndex: 'name', key: 'name', width: 200, ellipsis: true },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      // Bảng có scroll.x nên dùng table-layout: fixed — cắt chữ trong ô, di chuột xem đủ.
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value || '—'}
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (value: boolean) => (
        <Tag color={value ? 'green' : 'default'} style={{ borderRadius: 6, padding: '2px 8px' }}>
          {value ? 'Đang dùng' : 'Tắt'}
        </Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 165,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'}
        </span>
      ),
    },
    {
      title: 'Cập nhật',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 165,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'}
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 90,
      render: (_: any, record: LeaveType) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            aria-label="Sửa"
            onClick={() => setEditTarget(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <PageTitle
        title="Loại nghỉ phép"
        extra={
          <CustomButton
            bgColor="secondary-green"
            text="Thêm loại nghỉ"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          />
        }
      />
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={leaveTypes}
          loading={loading}
          scroll={{ x: 1020 }}
        />
      </div>
      <LeaveTypeModal
        open={createOpen}
        leaveType={null}
        onClose={() => setCreateOpen(false)}
        onSuccess={triggerRefresh}
      />
      <LeaveTypeModal
        open={!!editTarget}
        leaveType={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={triggerRefresh}
      />
    </div>
  );
};

export default LeaveTypeManager;
